import {
  Alert,
  Box,
  Button,
  CircularProgress,
  IconButton,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";

import AnalyzingProgress from "./AnalyzingProgress";

interface StudioExtractTabProps {
  prompt: string;
  setPrompt: (p: string) => void;
  isExtracting: boolean;
  isUploading: boolean;
  onReextract: () => void;
  draftExtraction: Record<string, unknown> | null;
  extractionError: string | null;
  onCopyJson: () => void;
}

export default function StudioExtractTab({
  prompt,
  setPrompt,
  isExtracting,
  isUploading,
  onReextract,
  draftExtraction,
  extractionError,
  onCopyJson,
}: StudioExtractTabProps) {
  return (
    <>
      <Box sx={{ padding: 2, borderBottom: "1px solid #EEE" }}>
        <Typography
          variant="caption"
          sx={{ fontWeight: 600, color: "text.secondary" }}
        >
          EXTRACTION PROMPT
        </Typography>
        <TextField
          multiline
          minRows={4}
          maxRows={8}
          fullWidth
          value={prompt}
          onChange={(e) => setPrompt(e.target.value)}
          size="small"
          sx={{ marginTop: 0.5 }}
        />
        <Stack direction="row" spacing={1} sx={{ marginTop: 1.5 }}>
          <Button
            variant="contained"
            size="small"
            disabled={isExtracting || isUploading}
            onClick={onReextract}
            startIcon={
              isExtracting ? (
                <CircularProgress size={14} sx={{ color: "inherit" }} />
              ) : null
            }
          >
            {isExtracting ? "Extracting…" : "Re-run extraction"}
          </Button>
        </Stack>
      </Box>

      <Box sx={{ flex: 1, overflow: "auto", padding: 2, minHeight: 0 }}>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          sx={{ marginBottom: 1 }}
        >
          <Typography
            variant="caption"
            sx={{ fontWeight: 600, color: "text.secondary" }}
          >
            DRAFT JSON
          </Typography>
          {draftExtraction && (
            <IconButton size="small" onClick={onCopyJson} title="Copy JSON">
              <Typography variant="caption">Copy</Typography>
            </IconButton>
          )}
        </Stack>

        {extractionError && (
          <Alert severity="error" sx={{ marginBottom: 2 }}>
            {extractionError}
          </Alert>
        )}

        {draftExtraction && (
          <Paper
            variant="outlined"
            sx={{
              padding: 1.5,
              backgroundColor: "#FAFAFA",
              fontFamily: '"Roboto Mono", monospace',
              fontSize: "0.8125rem",
              whiteSpace: "pre-wrap",
            }}
          >
            {JSON.stringify(draftExtraction, null, 2)}
          </Paper>
        )}
        {!draftExtraction && isExtracting && (
          <AnalyzingProgress phase="extracting" />
        )}
        {!draftExtraction && !isExtracting && !extractionError && (
          <Typography variant="body2" color="text.secondary">
            No extraction yet.
          </Typography>
        )}
      </Box>
    </>
  );
}
